import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import Button from "../../../../../shared/components/Button";
import SecondaryIcon from "../../../../../shared/components/SecondaryIcon";
import Icons from "../../../../../shared/constants/icon.constant";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from "@/shared/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { useUserBalances, useCryptoWithdrawalFee } from "../../../services";
import { useCreateCryptoWithdrawal } from "../../../services/crypto";
import type { WalletNetworkConfig } from "../../../interfaces/balance.interface";
import { isApiError } from "@/shared/api";
import { formatNetworkLabel } from "@/shared/utils/network-label";

interface WithdrawalFundsCryptoProps {
  onSubmit(): void;
}

const ALLOWED_NETWORKS = ["polygon", "ethereum"] as const;
const NETWORK_DISPLAY_NAMES: Record<string, string> = {
  polygon: "Polygon",
  ethereum: "Ethereum",
};

export default function WithdrawalFundsCrypto({
  onSubmit,
}: Readonly<WithdrawalFundsCryptoProps>) {
  const queryClient = useQueryClient();
  const {
    data: balancesData,
    isLoading: isLoadingBalances,
    error: balancesError,
  } = useUserBalances();

  const [selectedTokenId, setSelectedTokenId] = useState("");
  const [selectedNetworkKey, setSelectedNetworkKey] = useState("");
  const [amount, setAmount] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [memo, setMemo] = useState("");

  const cryptoBalances = useMemo(
    () => balancesData?.cryptoBalances ?? [],
    [balancesData?.cryptoBalances]
  );

  const tokenOptions = useMemo(
    () =>
      cryptoBalances.map((balance) => {
        const symbol = (balance.name ?? "").toUpperCase();
        const displayName = balance.name ?? symbol;

        return {
          label: symbol ? `${symbol} (${displayName})` : displayName,
          value: balance.id,
        };
      }),
    [cryptoBalances]
  );

  const selectedBalance = useMemo(
    () => cryptoBalances.find((item) => item.id === selectedTokenId) ?? null,
    [cryptoBalances, selectedTokenId]
  );

  const selectedTokenSymbol = (selectedBalance?.name ?? "").toUpperCase();
  const shouldSelectNetwork = selectedTokenSymbol === "USDC";

  const networkOptions = useMemo(() => {
    if (!selectedBalance || !shouldSelectNetwork) {
      return [];
    }

    const entries = Object.entries(selectedBalance.networkConfig ?? {}) as [
      string,
      WalletNetworkConfig,
    ][];

    const normalizedEntries = entries.map(([networkKey, config]) => {
      const lowerKey = networkKey.toLowerCase();
      const lowerName = (config?.name ?? "").toLowerCase();
      return {
        key: networkKey,
        lowerKey,
        config,
        lowerName,
      };
    });

    const options = ALLOWED_NETWORKS.map((network) => {
      const matched = normalizedEntries.find(
        (entry) => entry.lowerKey === network || entry.lowerName === network
      );

      const value = matched?.key?.toLowerCase() ?? network;
      const displayName =
        matched?.config?.name ??
        NETWORK_DISPLAY_NAMES[network] ??
        formatNetworkLabel(network);

      return {
        value,
        label: displayName,
      };
    }).filter((option, index, self) => {
      return self.findIndex((item) => item.value === option.value) === index;
    });

    return options;
  }, [selectedBalance, shouldSelectNetwork]);

  useEffect(() => {
    if (!tokenOptions.length) {
      setSelectedTokenId("");
      return;
    }

    if (!selectedTokenId) {
      setSelectedTokenId(tokenOptions[0]?.value ?? "");
      return;
    }

    if (!tokenOptions.some((option) => option.value === selectedTokenId)) {
      setSelectedTokenId(tokenOptions[0]?.value ?? "");
    }
  }, [tokenOptions, selectedTokenId]);

  useEffect(() => {
    if (!shouldSelectNetwork) {
      setSelectedNetworkKey("");
      return;
    }

    if (!networkOptions.length) {
      setSelectedNetworkKey("");
      return;
    }

    if (!selectedNetworkKey) {
      setSelectedNetworkKey(networkOptions[0]?.value ?? "");
      return;
    }

    if (!networkOptions.some((option) => option.value === selectedNetworkKey)) {
      setSelectedNetworkKey(networkOptions[0]?.value ?? "");
    }
  }, [
    networkOptions,
    selectedBalance,
    selectedNetworkKey,
    shouldSelectNetwork,
  ]);

  const shouldFetchFee =
    Boolean(selectedTokenSymbol) &&
    (!shouldSelectNetwork ? true : Boolean(selectedNetworkKey));

  const {
    data: withdrawalFee,
    isLoading: isLoadingFee,
    isError: isFeeError,
  } = useCryptoWithdrawalFee(
    {
      currency: selectedTokenSymbol.toLowerCase(),
      network: shouldSelectNetwork
        ? selectedNetworkKey || undefined
        : undefined,
    },
    {
      enabled: shouldFetchFee,
      staleTime: 30_000,
    }
  );

  const networkFeeValue =
    withdrawalFee?.networkFee !== undefined &&
    withdrawalFee?.networkFee !== null &&
    Number.isFinite(Number(withdrawalFee.networkFee))
      ? Number(withdrawalFee.networkFee)
      : null;

  const formattedNetworkFee =
    networkFeeValue !== null
      ? `${networkFeeValue} ${selectedTokenSymbol || ""}`.trim()
      : null;

  const networkPlaceholder = selectedBalance
    ? networkOptions.length
      ? "Select Network"
      : "No networks available"
    : "Select a currency first";

  const currencyPlaceholder = isLoadingBalances
    ? "Loading tokens..."
    : tokenOptions.length
      ? "Select Currency"
      : "No crypto wallets found";

  const amountNumber = Number.parseFloat(amount);
  const isValidAmount = Number.isFinite(amountNumber) && amountNumber > 0;

  const resolvedNetwork = useMemo(() => {
    if (shouldSelectNetwork) {
      return selectedNetworkKey || undefined;
    }

    const availableNetworks = Object.keys(selectedBalance?.networkConfig ?? {});
    if (availableNetworks.length > 0) {
      return availableNetworks[0];
    }

    return selectedTokenSymbol ? selectedTokenSymbol.toLowerCase() : undefined;
  }, [
    shouldSelectNetwork,
    selectedNetworkKey,
    selectedBalance?.networkConfig,
    selectedTokenSymbol,
  ]);

  const normalizedNetwork = resolvedNetwork?.toLowerCase();

  const createCryptoWithdrawal = useCreateCryptoWithdrawal();
  const isSubmitting = createCryptoWithdrawal.isPending;

  const isSubmitDisabled =
    isLoadingBalances ||
    !!balancesError ||
    !selectedTokenId ||
    (shouldSelectNetwork && (!selectedNetworkKey || !networkOptions.length)) ||
    !walletAddress.trim() ||
    !isValidAmount ||
    isSubmitting;

  const handleWithdraw = () => {
    if (isSubmitDisabled || !selectedTokenSymbol) {
      return;
    }

    const trimmedAddress = walletAddress.trim();
    if (!trimmedAddress) {
      toast.error("Destination wallet address is required.");
      return;
    }

    if (!isValidAmount) {
      toast.error("Enter a valid withdrawal amount.");
      return;
    }

    const payloadCurrency = selectedTokenSymbol.toLowerCase();

    createCryptoWithdrawal.mutate(
      {
        currency: payloadCurrency,
        amount: amountNumber,
        walletAddress: trimmedAddress,
        network: normalizedNetwork,
        memo: memo.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Withdrawal request submitted successfully.");
          setAmount("");
          setWalletAddress("");
          setMemo("");
          void queryClient.invalidateQueries({
            queryKey: ["cryptoWithdrawalHistory"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["fiatWithdrawalHistory"],
          });
          onSubmit();
        },
        onError: (error: unknown) => {
          let message = "Failed to submit withdrawal request.";
          let requestId: string | undefined;

          if (isApiError(error)) {
            message = error.message;
            requestId = error.requestId;
          } else if (error instanceof Error) {
            message = error.message;
          }

          toast.error(message, {
            description: requestId ? `Request ID: ${requestId}` : undefined,
          });
        },
      }
    );
  };

  return (
    <div className="space-y-3">
      <FieldSet>
        <FieldGroup>
          <Field>
            <FieldLabel>Cryptocurrency</FieldLabel>
            <FieldContent>
              <Select
                value={selectedTokenId || undefined}
                onValueChange={setSelectedTokenId}
                disabled={isLoadingBalances || !tokenOptions.length}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={currencyPlaceholder} />
                </SelectTrigger>
                <SelectContent align="start" className="min-w-44">
                  {tokenOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldDescription>
                Choose the asset you want to withdraw to your external wallet.
              </FieldDescription>
            </FieldContent>
          </Field>

          {shouldSelectNetwork ? (
            <Field>
              <FieldLabel>Network</FieldLabel>
              <FieldContent>
                <Select
                  value={selectedNetworkKey || undefined}
                  onValueChange={setSelectedNetworkKey}
                  disabled={!networkOptions.length}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={networkPlaceholder} />
                  </SelectTrigger>
                  <SelectContent align="start" className="min-w-44">
                    {networkOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldDescription>
                  Supported networks: Polygon or Ethereum. Make sure your
                  destination address matches this network.
                </FieldDescription>
              </FieldContent>
            </Field>
          ) : null}

          <Field>
            <FieldLabel>Amount</FieldLabel>
            <FieldContent>
              <div className="relative">
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="pr-14"
                />
                <span className="text-muted-foreground absolute inset-y-0 right-3 flex items-center text-xs">
                  {selectedTokenSymbol || "TOKEN"}
                </span>
              </div>
              <FieldDescription>
                Enter the amount you would like to withdraw.
              </FieldDescription>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>Wallet Address</FieldLabel>
            <FieldContent>
              <Textarea
                value={walletAddress}
                onChange={(event) => setWalletAddress(event.target.value)}
                placeholder="0xi7121573dfd21e1132e3e4y9k1d3a2d62a32ea53kg18324"
                className="min-h-20"
              />
              <FieldDescription>
                Paste the destination wallet address. Double-check for typos
                before submitting.
              </FieldDescription>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>Memo (Optional)</FieldLabel>
            <FieldContent>
              <Textarea
                value={memo}
                onChange={(event) => setMemo(event.target.value)}
                placeholder="Reference or note for this withdrawal"
                className="min-h-16"
              />
            </FieldContent>
          </Field>
        </FieldGroup>
      </FieldSet>

      {balancesError ? (
        <p className="text-xs text-red-400">
          {balancesError?.message ?? "Failed to load wallet data."}
        </p>
      ) : null}

      {isFeeError ? (
        <p className="text-xs text-red-400">
          Unable to fetch network fee. Retry after selecting a supported
          network.
        </p>
      ) : null}

      <div>
        <Button
          className="w-full"
          onClick={handleWithdraw}
          disabled={isSubmitDisabled}>
          {isSubmitting ? "Processing…" : "Withdraw"}
        </Button>
      </div>

      <div className="text-xs text-label-dark space-y-4 pt-4">
        <p>
          By Clicking the Withdraw button, I agree to the Terms of the{" "}
          <b className="text-white">Customer Agreement</b> and the{" "}
          <b className="text-white">Anti-Money Laundering Policy</b>, and
          confirm that the{" "}
          <b className="text-white">
            payment will not be made from a third-party account
          </b>
          .
        </p>
        <div className="border-t border-gray-700 border-dashed"></div>
        <div className="flex items-center gap-2 mb-2">
          <SecondaryIcon
            icon={Icons.DepositAmountIcon({ width: 15, height: 15 })}
            isActive
          />
          <p className="text-white">
            Network Fee:{" "}
            {isLoadingFee
              ? "Calculating..."
              : (formattedNetworkFee ?? "Data unavailable")}
          </p>
        </div>
        <div className="border-t border-gray-700 border-dashed"></div>
        <div className="flex items-center gap-2 mb-2">
          <SecondaryIcon
            icon={Icons.TimeIcon({ width: 15, height: 15 })}
            isActive
          />
          <p className="text-white">
            Processing time: Up to 2 hours (may vary)
          </p>
        </div>
        <div className="border-t border-gray-700 border-dashed"></div>
        <div className="flex items-center gap-2 mb-2">
          <SecondaryIcon
            icon={Icons.InfoIcon({ width: 15, height: 15 })}
            isActive
          />
          <a href="#" className="text-white underline">
            Additional info.
          </a>
        </div>
      </div>
    </div>
  );
}
